import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithPopup, GoogleAuthProvider, OAuthProvider, getAdditionalUserInfo } from 'firebase/auth';
import { auth } from '../firebase';
import { Container, Paper, Typography, Button, Alert, Box, CircularProgress, Divider } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { TDSYNNEXLogo } from '../components/core/TDSYNNEXLogo';
import { saveUserProfile } from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';
import {
    extractEntraOrganizationAttributes,
    fetchEntraGraphMeProfile,
} from '../utils/entraProfileMapper';
import type { UserProfile } from '../types';

const googleProvider = new GoogleAuthProvider();

// Correctly configure the Microsoft provider with the specific tenant ID from environment variables
const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.addScope('User.Read');
const tenantId = import.meta.env.VITE_MS_TENANT_ID;

if (tenantId) {
    microsoftProvider.setCustomParameters({
        tenant: tenantId,
        // Force account selection to avoid auto-login loops with wrong accounts
        prompt: 'select_account' 
    });
}

// Check if the app is running in the development environment using the Vite environment variable.
const isDevEnvironment = import.meta.env.VITE_FIREBASE_PROJECT_ID === 'td-synapse-dev';

export default function LoginPage() {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { refreshUser } = useAuth();

    const handleGoogleSignIn = () => {
        setLoading(true);
        setError('');
        signInWithPopup(auth, googleProvider)
            .then(() => {
                navigate('/');
            })
            .catch((err) => {
                console.error("Google Sign In Error:", err);
                setError("Failed to sign in with Google. Please try again.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleMicrosoftSignIn = () => {
        setLoading(true);
        setError('');
        signInWithPopup(auth, microsoftProvider)
            .then(async (result) => {
                const credential = OAuthProvider.credentialFromResult(result);
                const additionalInfo = getAdditionalUserInfo(result);
                if (credential?.accessToken) {
                    try {
                        const graphProfile = await fetchEntraGraphMeProfile(credential.accessToken);
                        const extracted = extractEntraOrganizationAttributes(
                            graphProfile,
                            (additionalInfo?.profile as Record<string, unknown> | undefined) ?? null,
                        );

                        console.group('[Entra ID /me Inspection]');
                        console.log('Raw Microsoft Graph /v1.0/me response:', graphProfile);
                        console.log(
                            'Non-empty onPremisesExtensionAttributes:',
                            extracted.entraAttributesSnapshot?.extensionAttributes ?? {},
                        );
                        console.log('Resolved Synapse analytics fields:', {
                            country: extracted.country ?? null,
                            countryCode: extracted.countryCode ?? null,
                            region: extracted.region ?? null,
                            legalEntity: extracted.legalEntity ?? null,
                        });
                        console.groupEnd();

                        const updates: Partial<UserProfile> = {};
                        if (extracted.country) updates.country = extracted.country;
                        if (extracted.countryCode) updates.countryCode = extracted.countryCode;
                        if (extracted.region) updates.region = extracted.region;
                        if (extracted.legalEntity) updates.legalEntity = extracted.legalEntity;
                        if (extracted.entraAttributesSnapshot) {
                            updates.entraAttributesSnapshot = extracted.entraAttributesSnapshot;
                        }

                        if (Object.keys(updates).length > 0) {
                            try {
                                await saveUserProfile(result.user.uid, updates);
                                await refreshUser();
                            } catch (syncErr) {
                                console.debug('Deferring Entra profile sync until user document exists:', syncErr);
                                try {
                                    sessionStorage.setItem(
                                        `synapse_pending_entra_profile_${result.user.uid}`,
                                        JSON.stringify(updates),
                                    );
                                } catch {
                                    // Ignore storage errors
                                }
                            }
                        }
                    } catch (inspectErr) {
                        console.warn('Could not inspect Entra /me profile on client:', inspectErr);
                    }
                }
                navigate('/');
            })
            .catch((err) => {
                console.error("Microsoft Sign In Error:", err);
                setError("Failed to sign in with Microsoft. Please ensure you are using your corporate account.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Container component="main" maxWidth="xs">
            <Paper elevation={3} sx={{ marginTop: 8, padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ mb: 3 }}>
                    <TDSYNNEXLogo />
                </Box>
                <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
                    Synapse Login
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ width: '100%', mt: 1 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={<img src="/assets/images/TD_SYNNEX_Logo_WHT.png" alt="TD SYNNEX" style={{ height: '24px', width: 'auto' }} />}
                        onClick={handleMicrosoftSignIn}
                        disabled={loading || !tenantId}
                        sx={{ 
                            mb: 2, 
                            backgroundColor: '#2F2F2F', 
                            '&:hover': { backgroundColor: '#000000' },
                            textTransform: 'none'
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign in with SSO'}
                    </Button>

                    {!tenantId && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Microsoft Tenant ID not configured.
                        </Alert>
                    )}
                    
                    {isDevEnvironment && (
                        <>
                            <Divider sx={{ my: 2 }}>OR (Dev Only)</Divider>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<GoogleIcon />}
                                onClick={handleGoogleSignIn}
                                disabled={loading}
                                sx={{ textTransform: 'none' }}
                            >
                                Sign in with Google
                            </Button>
                        </>
                    )}
                </Box>
            </Paper>
        </Container>
    );
}
